import React from 'react';
import { Box, Card, CardContent, CardHeader, Typography, Stack } from '@mui/material';
import { useField } from 'react-final-form';

import { TextField } from '@occc-app/design-system';

import { AssetImageUpload } from './asset-image-upload';

type AssetCreateFormProps = {
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
};

type AssetImageUploadFieldProps = {
  aspectRatio: '4x3' | '1x1' | '9x16';
  imageFieldName: string;
  urlFieldName: string;
  required?: boolean;
};

const AssetImageUploadField = ({
  aspectRatio,
  imageFieldName,
  urlFieldName,
  required = false,
}: AssetImageUploadFieldProps) => {
  const imageField = useField<File | null>(imageFieldName);
  const urlField = useField<string | null>(urlFieldName);

  return (
    <AssetImageUpload
      aspectRatio={aspectRatio}
      fieldName={imageFieldName}
      value={imageField.input.value}
      onChange={imageField.input.onChange}
      urlValue={urlField.input.value}
      onUrlChange={urlField.input.onChange}
      error={imageField.meta.touched && imageField.meta.error ? imageField.meta.error : undefined}
      required={required}
    />
  );
};

export const AssetCreateForm = ({ handleSubmit }: AssetCreateFormProps) => {
  return (
    <form onSubmit={handleSubmit}>
      <Box mb={2}>
        <Card>
          <CardHeader
            title={
              <Typography variant='subtitle1' paragraph>
                Asset Details
              </Typography>
            }
          />
          <CardContent>
            <Stack direction='row' spacing={3}>
              <TextField name='name' label='Asset Name' required />
              <TextField name='description' label='Asset Description' required />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box mb={2}>
        <Card>
          <CardHeader
            title={
              <Typography variant='subtitle1' paragraph>
                Asset Images
              </Typography>
            }
          />
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 4, sm: 0 }}
              justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              flexWrap='wrap'
            >
              <AssetImageUploadField
                aspectRatio='4x3'
                imageFieldName='landscapeImage'
                urlFieldName='landscapeImageUrl'
              />
              <AssetImageUploadField
                aspectRatio='1x1'
                imageFieldName='squareImage'
                urlFieldName='squareImageUrl'
                required
              />
              <AssetImageUploadField
                aspectRatio='9x16'
                imageFieldName='portraitImage'
                urlFieldName='portraitImageUrl'
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </form>
  );
};
